import { DatePicker } from '@/components/ui/date-picker';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatCurrency, formatDate, getTodayDate } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  Wallet,
  Filter,
  Zap,
  Droplets,
  Sparkles,
  Shield,
  Wrench
} from 'lucide-react';
import Pagination from '../components/Pagination';
import { MobileCardContainer, MobileCard, MobileCardHeader, MobileCardTitle, MobileCardContent, MobileCardRow, MobileCardFooter } from '../components/MobileCard';

import CurrencyInput from '../components/CurrencyInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 10;

const categoryConfig = {
  listrik_gedung: { label: 'Listrik Gedung', icon: Zap, color: 'text-yellow-600' },
  air_gedung: { label: 'Air Gedung', icon: Droplets, color: 'text-blue-600' },
  kebersihan: { label: 'Kebersihan', icon: Sparkles, color: 'text-green-600' },
  keamanan: { label: 'Keamanan', icon: Shield, color: 'text-purple-600' },
  maintenance: { label: 'Maintenance', icon: Wrench, color: 'text-orange-600' },
  lainnya: { label: 'Lainnya', icon: Wallet, color: 'text-gray-600' },
};

const ExpenseForm = ({ expense, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    category: expense?.category || 'listrik_gedung',
    amount: expense?.amount || '',
    description: expense?.description || '',
    date: expense?.date || getTodayDate()
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.date) {
      toast.error('Mohon isi jumlah dan tanggal');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (expense?.id) {
        await axios.put(`${API_URL}/api/expenses/${expense.id}`, payload);
        toast.success('Biaya berhasil diperbarui');
      } else {
        await axios.post(`${API_URL}/api/expenses`, payload);
        toast.success('Biaya berhasil ditambahkan');
      }
      onSave();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan biaya');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="category">Kategori *</Label>
        <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
          <SelectTrigger className="h-12 bg-muted border-0 rounded-xl" data-testid="expense-category-select">
            <SelectValue placeholder="Pilih kategori" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(categoryConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="amount">Jumlah *</Label>
          <CurrencyInput
            id="amount"
            name="amount"
            placeholder="0"
            value={formData.amount}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
            data-testid="expense-amount-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="date">Tanggal *</Label>
          <DatePicker
            id="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
            data-testid="expense-date-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Keterangan</Label>
        <Textarea
          id="description"
          name="description"
          placeholder="Keterangan tambahan..."
          value={formData.description}
          onChange={handleChange}
          className="bg-muted border-0 rounded-xl resize-none"
          rows={3}
          data-testid="expense-description-input"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
          Batal
        </Button>
        <Button type="submit" disabled={loading} className="rounded-xl" data-testid="expense-submit-btn">
          {loading ? <div className="w-5 h-5 spinner" /> : expense?.id ? 'Simpan' : 'Tambah'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const BiayaOperasional = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ total: 0, byCategory: {} });

  const fetchExpenses = async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      params.append('limit', ITEMS_PER_PAGE.toString());
      params.append('skip', (page * ITEMS_PER_PAGE).toString());
      
      const [response, allExpensesRes] = await Promise.all([
        axios.get(`${API_URL}/api/expenses?${params.toString()}`),
        // Get all expenses for stats
        axios.get(`${API_URL}/api/expenses?limit=1000`)
      ]);
      
      setExpenses(response.data.data || []);
      setTotalItems(response.data.total || 0);
      
      // Calculate stats from all expenses
      const allExpenses = allExpensesRes.data.data || [];
      const totalAmount = allExpenses.reduce((sum, e) => sum + e.amount, 0);
      const byCategory = allExpenses.reduce((acc, e) => {
        acc[e.category] = (acc[e.category] || 0) + e.amount;
        return acc;
      }, {});
      setStats({ total: totalAmount, byCategory });
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Gagal memuat data biaya');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, page]);

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setDialogOpen(true);
  };

  const handleDelete = (expense) => {
    setSelectedExpense(expense);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/expenses/${selectedExpense.id}`);
      toast.success('Biaya berhasil dihapus');
      fetchExpenses();
    } catch (error) {
      toast.error('Gagal menghapus biaya');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedExpense(null);
    }
  };

  const handleSave = () => {
    setDialogOpen(false);
    setSelectedExpense(null);
    fetchExpenses();
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="biaya-operasional-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Biaya Operasional</h1>
          <p className="text-muted-foreground mt-1">Kelola pengeluaran operasional kost</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="rounded-xl" data-testid="add-expense-btn">
          <Plus className="w-5 h-5 mr-2" />
          Tambah Biaya
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="p-3 md:p-4 col-span-2">
          <p className="text-2xl md:text-3xl font-bold text-accent truncate">{formatCurrency(stats.total)}</p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Pengeluaran</p>
        </Card>
        {Object.entries(stats.byCategory).slice(0, 2).map(([cat, amount]) => {
          const config = categoryConfig[cat] || categoryConfig.lainnya;
          const Icon = config.icon;
          return (
            <Card key={cat} className="p-3 md:p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${config.color}`} />
                <span className="text-xs text-muted-foreground truncate">{config.label}</span>
              </div>
              <p className="text-lg md:text-xl font-bold truncate">{formatCurrency(amount)}</p>
            </Card>
          );
        })}
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filter:</span>
        </div>
        <Select value={filterCategory || "all"} onValueChange={(v) => { setFilterCategory(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-[180px] h-10 bg-muted border-0 rounded-xl" data-testid="filter-category-select">
            <SelectValue placeholder="Semua Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {Object.entries(categoryConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <Card className="p-6">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        </Card>
      ) : expenses.length === 0 ? (
        <Card className="p-8">
          <div className="empty-state">
            <Wallet className="empty-state-icon" />
            <p className="empty-state-title">Belum ada biaya operasional</p>
            <p className="empty-state-description">Catat pengeluaran operasional Anda</p>
          </div>
        </Card>
      ) : (
        <>
          {/* Mobile Cards View */}
          <MobileCardContainer>
            {expenses.map((expense) => {
              const config = categoryConfig[expense.category] || categoryConfig.lainnya;
              const Icon = config.icon;
              return (
                <MobileCard key={expense.id} testId={`expense-card-${expense.id}`}>
                  <MobileCardHeader>
                    <MobileCardTitle>
                      <div className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${config.color}`} />
                        <span className="font-semibold">{config.label}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{formatDate(expense.date)}</p>
                    </MobileCardTitle>
                    <span className="text-lg font-bold text-accent">{formatCurrency(expense.amount)}</span>
                  </MobileCardHeader>
                  {expense.description && (
                    <MobileCardContent>
                      <p className="text-sm text-muted-foreground">{expense.description}</p>
                    </MobileCardContent>
                  )}
                  <MobileCardFooter>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(expense)}>
                      <Pencil className="w-3 h-3 mr-1" />Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(expense)} className="text-destructive border-destructive/50">
                      <Trash2 className="w-3 h-3 mr-1" />Hapus
                    </Button>
                  </MobileCardFooter>
                </MobileCard>
              );
            })}
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
              onPageChange={setPage}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </MobileCardContainer>

          {/* Desktop Table View */}
          <Card className="overflow-hidden hidden md:block">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead>Keterangan</TableHead>
                  <TableHead className="text-right">Jumlah</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => {
                  const config = categoryConfig[expense.category] || categoryConfig.lainnya;
                  const Icon = config.icon;
                  return (
                    <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                      <TableCell>{formatDate(expense.date)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${config.color}`} />
                          <span>{config.label}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{expense.description || '-'}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(expense.amount)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(expense)}>
                              <Pencil className="w-4 h-4 mr-2" />Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(expense)} className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />Hapus
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
              onPageChange={setPage}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </Card>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedExpense ? 'Edit Biaya' : 'Tambah Biaya Baru'}
            </DialogTitle>
            <DialogDescription>
              {selectedExpense ? 'Perbarui data biaya operasional' : 'Catat pengeluaran operasional'}
            </DialogDescription>
          </DialogHeader>
          <ExpenseForm
            expense={selectedExpense}
            onSave={handleSave}
            onClose={() => { setDialogOpen(false); setSelectedExpense(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Biaya?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda yakin ingin menghapus data biaya ini?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BiayaOperasional;
